/* eslint-disable no-console */
/* eslint-disable consistent-return */
import get from 'lodash/get';
import { API_IN_PROGRESS, GET_QUESTION_FAILURE, SET_ACTION_MESSAGE } from '../types';
// eslint-disable-next-line import/no-cycle
import { axiosInstance } from '../../util/axios';
import LocalStorageServices from '../../util/localStorageServices';
import { shouldEnableLogin } from '../../utils';

/**
 * @description Fetches Auth Tokens and stores them to LocalStorage.
 */
export const getJWTAuthToken = (
  url,
  reqParams,
  params = {
    redirectTo: '/',
  },
) => {
  return async (dispatch, getState) => {
    const state = getState();
    const hasLoginEnabled = shouldEnableLogin(get(state, 'auth.login', {}));
    dispatch({
      type: API_IN_PROGRESS,
    });
    await axiosInstance
      .post(url, reqParams)
      .then((response) => {
        const accessToken = get(response, 'data.access_token', null);
        const expiresOn = get(response, 'data.expires_on');
        const refreshToken = get(response, 'data.refresh_token', null);
        const item = {
          value: accessToken,
          expiry: expiresOn,
          refresh: refreshToken,
        };
        if (accessToken) {
          LocalStorageServices.setAccessToken(JSON.stringify(item));
          LocalStorageServices.setSsoAccessTokenAndExpiry(response);
          if (hasLoginEnabled) {
            const redirectUrl = window.location.origin + params.redirectTo;
            window.location.replace(redirectUrl);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch({
          type: GET_QUESTION_FAILURE,
        });
        if (error.response) {
          const errorMessage = get(error.response, 'data.errors.message', 'Some Error Occured!');
          dispatch({
            type: SET_ACTION_MESSAGE,
            payload: {
              actionMessage: {
                type: 'error',
                message: errorMessage,
              },
            },
          });
        }
      });
    return !!LocalStorageServices.getAccessToken();
  };
};

/**
 * @description Performs /auth API call and URL redirection.
 */
export const triggerAuthAction = () => {
  const { pathname, search } = window.location;
  const URL = pathname.concat(search);
  return async (dispatch, getState) => {
    const state = getState();
    const loginURL = get(state, 'auth.login.loginURL', '/auth');
    const reqParams = {
      carrier: process.env.REACT_APP_CARRIER_ID,
    };
    const hasAuthToken = await dispatch(getJWTAuthToken(loginURL, reqParams, { redirectTo: URL }));
    return hasAuthToken;
  };
};

/**
 * @description Fetches Auth and stores token & return access token
 */
const getTokensFromAuth = async (loginUrl) => {
  const loginURL = loginUrl || '/auth';
  const reqParams = {
    carrier: process.env.REACT_APP_CARRIER_ID,
  };
  await axiosInstance.post(loginURL, reqParams).then((response) => {
    const accessToken = get(response, 'data.access_token', null);
    const expiresOn = get(response, 'data.expires_on');
    const refreshToken = get(response, 'data.refresh_token', null);
    const item = {
      value: accessToken,
      expiry: expiresOn,
      refresh: refreshToken,
    };
    if (accessToken) {
      LocalStorageServices.setAccessToken(JSON.stringify(item));
      LocalStorageServices.setSsoAccessTokenAndExpiry(response);
    }
  });
  return LocalStorageServices.getAccessToken();
};

/**
 * @description Performs auth API call, fetches new tokens and stores them in LocalStorage.
 */
export const getAndStoreTokensFromAuth = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const loginURL = get(state, 'auth.login.loginURL', '/auth');
    dispatch({
      type: API_IN_PROGRESS,
    });

    await getTokensFromAuth(loginURL).catch((error) => {
      console.error(error);
      dispatch({
        type: GET_QUESTION_FAILURE,
      });
      if (error.response) {
        const errorMessage = get(error.response, 'data.errors.message', 'Some Error Occured!');
        dispatch({
          type: SET_ACTION_MESSAGE,
          payload: {
            actionMessage: {
              type: 'error',
              message: errorMessage,
            },
          },
        });
      }
    });
    return LocalStorageServices.getAccessToken();
  };
};

// Refresh Access Token and return fresh access token
// If no refresh token available, make auth call & return new access token
export const refreshAccessToken = async (refreshUrl) => {
  const refreshURL = refreshUrl || '/auth/refresh_token';
  const refreshToken = LocalStorageServices.getRefreshToken();

  if (refreshToken) {
    const reqParams = {
      refresh_token: refreshToken,
    };
    await axiosInstance.post(refreshURL, reqParams).then((res) => {
      const _accessToken = get(res, 'data.access_token', null);
      const _expiresOn = get(res, 'data.expires_on');
      const _refreshToken = get(res, 'data.refresh_token', null);
      const item = {
        value: _accessToken,
        expiry: _expiresOn,
        refresh: _refreshToken,
      };
      if (_accessToken) {
        LocalStorageServices.clearToken();
        LocalStorageServices.setAccessToken(JSON.stringify(item));
        LocalStorageServices.setSsoAccessTokenAndExpiry(res);
      }
    });
  } else {
    await getTokensFromAuth();
  }

  return LocalStorageServices.getAccessToken();
};

/**
 * @description Performs refresh_token API call, fetches new tokens and stores them in LocalStorage.
 */
export const refreshAndStoreAccessToken = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const refreshURL = get(state, 'auth.login.refreshURL', '/auth/refresh_token');
    const refreshToken = LocalStorageServices.getRefreshToken();
    if (refreshToken) {
      await refreshAccessToken(refreshURL).catch(() => {
        const isLoginPageEnabled = shouldEnableLogin(get(state, 'auth.login', {}));
        if (!isLoginPageEnabled) {
          dispatch(triggerAuthAction());
        } else {
          window.location.reload();
        }
      });
    } else {
      const isLoginPageEnabled = shouldEnableLogin(get(state, 'auth.login', {}));
      if (!isLoginPageEnabled) {
        LocalStorageServices.clearToken();
        await dispatch(getAndStoreTokensFromAuth());
      }
    }
    return LocalStorageServices.getAccessToken();
  };
};

/**
 * @description Perform logout API call and removes Auth tokens from LocalStorage.
 * Also take care of retry logics.
 */
const removeJWTAuthToken = (url, reqParams) => {
  return (dispatch, getState) => {
    dispatch({
      type: API_IN_PROGRESS,
    });
    axiosInstance
      .post(url, reqParams)
      .then((response) => {
        if (response) {
          LocalStorageServices.clearToken();
          window.location.replace('/');
        }
      })
      .catch(async (error) => {
        console.error(error);
        const refreshRetry = get(error, 'response.config._retry', false);
        if (refreshRetry) {
          const state = getState();
          const logoutURL = get(state, 'auth.login.logoutURL', '/auth/logout');
          const originalRequest = error.config;
          const accessToken = await dispatch(refreshAndStoreAccessToken());
          if (accessToken) {
            // Replace with new access_token value present in the case of logout.
            if (originalRequest.data && originalRequest.url.includes(logoutURL)) {
              let requestData = originalRequest.data;
              requestData = JSON.parse(requestData);
              requestData.access_token = accessToken;
              originalRequest.data = requestData;
            }
            axiosInstance.post(originalRequest.url, originalRequest.data).then((response) => {
              if (response) {
                LocalStorageServices.clearToken();
                window.location.replace('/');
              }
            });
          }
        } else {
          dispatch({
            type: GET_QUESTION_FAILURE,
          });
          dispatch({
            type: SET_ACTION_MESSAGE,
            payload: {
              actionMessage: {
                type: 'error',
                message: '',
              },
            },
          });
        }
      });
  };
};

/**
 * @description Removes Auth Token from local storage and redirects user to homepage (/ by default)
 */
export const removeAuthHeaders = () => {
  return (dispatch, getState) => {
    const state = getState();
    const logoutURL = get(state, 'auth.login.logoutURL', '/auth/logout');
    const accessToken = LocalStorageServices.getAccessToken();
    if (accessToken) {
      const reqParams = {
        access_token: accessToken,
      };
      dispatch(removeJWTAuthToken(logoutURL, reqParams));
    } else {
      window.location.replace('/');
    }
  };
};
